import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { hoppSolidConfig } from '../components/hoppsolid/hoppSolidConfig';

// HOPP Solid — base structure page.
// Renders the five anchor questions, technology cards and contractor reference
// sections from hoppSolidConfig. All content fields are placeholders until the
// real copy arrives — components already handle filled vs. empty states.

export default function HoppSolid() {
  const { seo, brand, hero, sections, technologies, contractors } = hoppSolidConfig;

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: brand.darkBg }}>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
      </Helmet>

      {/* Hero + anchor questions */}
      <header className="px-5 pt-14 pb-10 max-w-3xl mx-auto text-center">
        <p className="text-[11px] font-bold tracking-widest mb-3" style={{ color: brand.primaryColor }}>
          {hero.eyebrow}
        </p>
        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3">{hero.title}</h1>
        <p className="text-sm text-white/60 mb-8">{hero.tagline}</p>
        <nav className="flex flex-wrap justify-center gap-2">
          {hero.questions.map((q) => (
            <a
              key={q.id}
              href={`#${q.id}`}
              className="text-[11px] font-semibold text-white/80 bg-white/[0.06] border border-white/10 rounded-full px-4 py-2 hover:bg-white/[0.1] transition-colors"
            >
              {q.label}
            </a>
          ))}
        </nav>
      </header>

      <main className="px-5 pb-16 max-w-3xl mx-auto flex flex-col gap-12">
        <ContentSection id="mit-epitunk" title={sections.mitEpitunk.title} content={sections.mitEpitunk.content} />

        <section id="technologiak">
          <h2 className="text-2xl font-extrabold text-white mb-6">MILYEN TECHNOLÓGIÁKKAL ÉPÍTÜNK?</h2>
          <div className="flex flex-col gap-3">
            {technologies.map((tech) => (
              <TechnologyCard key={tech.id} tech={tech} brand={brand} />
            ))}
          </div>
        </section>

        <ContentSection id="keszultseg" title={sections.keszultseg.title} content={sections.keszultseg.content} />

        <section id="kik-epitik">
          <h2 className="text-2xl font-extrabold text-white mb-6">KIK ÉPÍTIK?</h2>
          <div className="flex flex-col gap-4">
            {contractors.map((contractor) => (
              <ContractorCard key={contractor.id} contractor={contractor} brand={brand} />
            ))}
          </div>
        </section>

        <section id="referenciak">
          <h2 className="text-2xl font-extrabold text-white mb-6">REFERENCIÁK</h2>
          <ReferenceGrid contractors={contractors} />
        </section>
      </main>
    </div>
  );
}

function ContentSection({ id, title, content }) {
  return (
    <section id={id}>
      <h2 className="text-2xl font-extrabold text-white mb-4">{title}</h2>
      {content ? <BodyContent content={content} /> : <PlaceholderText />}
    </section>
  );
}

function BodyContent({ content }) {
  if (Array.isArray(content)) {
    return (
      <ul className="list-disc pl-5 flex flex-col gap-1.5 text-sm text-white/70 leading-relaxed">
        {content.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    );
  }
  return <p className="text-sm text-white/70 leading-relaxed">{content}</p>;
}

function PlaceholderText() {
  return (
    <p className="text-sm italic text-white/40 border border-dashed border-white/15 rounded-2xl px-4 py-4">
      A részletes tartalom hamarosan érkezik.
    </p>
  );
}

function AccordionBlock({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left"
      >
        <span className="text-xs font-bold text-white/80 tracking-wide">{title}</span>
        <span
          className="text-white/50 text-lg leading-none transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'none' }}
        >
          +
        </span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function TechnologyCard({ tech, brand }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="rounded-2xl border border-white/10 overflow-hidden"
      style={{ backgroundColor: brand.cardBg }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-base font-bold text-white">{tech.name}</span>
        <span
          className="text-white/50 text-xl leading-none transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'none' }}
        >
          +
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 flex flex-col gap-3">
          {tech.intro ? <BodyContent content={tech.intro} /> : <PlaceholderText />}

          {tech.advantages?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-white/50 tracking-wide mb-2">ELŐNYÖK</p>
              <div className="flex flex-wrap gap-2">
                {tech.advantages.map((adv, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-semibold text-white/80 bg-white/[0.06] border border-white/10 rounded-full px-3 py-1.5"
                  >
                    {adv}
                  </span>
                ))}
              </div>
            </div>
          )}

          <AccordionBlock title="RÉSZLETES MŰSZAKI TARTALOM">
            {tech.shellSpec || tech.turnkeySpec ? (
              <div className="flex flex-col gap-3">
                {tech.shellSpec && (
                  <div>
                    <p className="text-[11px] font-bold text-white/50 tracking-wide mb-1">SZERKEZETKÉSZ</p>
                    <BodyContent content={tech.shellSpec} />
                  </div>
                )}
                {tech.turnkeySpec && (
                  <div>
                    <p className="text-[11px] font-bold text-white/50 tracking-wide mb-1">KULCSRAKÉSZ</p>
                    <BodyContent content={tech.turnkeySpec} />
                  </div>
                )}
              </div>
            ) : (
              <PlaceholderText />
            )}
          </AccordionBlock>

          {tech.images?.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {tech.images.map((img, i) => (
                <img key={i} src={img.src} alt={img.alt || tech.name} className="rounded-xl w-full object-cover" />
              ))}
            </div>
          )}

          {tech.references?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-white/50 tracking-wide mb-2">REFERENCIÁK</p>
              <div className="flex flex-col gap-2">
                {tech.references.map((ref, i) => (
                  <div key={i} className="bg-white/[0.04] border border-white/10 rounded-xl p-3">
                    <p className="text-sm font-semibold text-white">{ref.title}</p>
                    {ref.description && <p className="text-xs text-white/60 mt-1">{ref.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContractorCard({ contractor, brand }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="rounded-2xl border border-white/10 overflow-hidden"
      style={{ backgroundColor: brand.cardBg }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-base font-bold text-white">{contractor.name}</span>
        <span
          className="text-white/50 text-xl leading-none transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'none' }}
        >
          +
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 flex flex-col gap-3">
          {contractor.intro ? <BodyContent content={contractor.intro} /> : <PlaceholderText />}

          {contractor.technologies?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-white/50 tracking-wide mb-2">ALKALMAZOTT TECHNOLÓGIÁK</p>
              <div className="flex flex-wrap gap-2">
                {contractor.technologies.map((techId) => (
                  <span
                    key={techId}
                    className="text-[11px] font-semibold text-white/80 bg-white/[0.06] border border-white/10 rounded-full px-3 py-1.5"
                  >
                    {techId}
                  </span>
                ))}
              </div>
            </div>
          )}

          {contractor.references?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-white/50 tracking-wide mb-2">REFERENCIA MUNKÁK</p>
              <div className="flex flex-col gap-2">
                {contractor.references.map((ref, i) => (
                  <div key={i} className="bg-white/[0.04] border border-white/10 rounded-xl p-3">
                    <p className="text-sm font-semibold text-white">{ref.title}</p>
                    {ref.description && <p className="text-xs text-white/60 mt-1">{ref.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <AccordionBlock title="KAPCSOLÓDÓ MŰSZAKI TARTALOM">
            {contractor.technicalContent ? (
              <BodyContent content={contractor.technicalContent} />
            ) : (
              <PlaceholderText />
            )}
          </AccordionBlock>
        </div>
      )}
    </div>
  );
}

function ReferenceGrid({ contractors }) {
  const allRefs = contractors.flatMap((c) =>
    (c.references || []).map((ref) => ({ ...ref, contractor: c.name }))
  );

  if (allRefs.length === 0) {
    return <PlaceholderText />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {allRefs.map((ref, i) => (
        <div key={i} className="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden">
          {ref.image && <img src={ref.image} alt={ref.title} className="w-full aspect-video object-cover" />}
          <div className="p-4">
            <p className="text-sm font-semibold text-white">{ref.title}</p>
            <p className="text-[11px] text-white/50 mt-0.5">{ref.contractor}</p>
            {ref.description && <p className="text-xs text-white/60 mt-2">{ref.description}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
